import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableProductItemWeightAndDimensions } from '../../../../../../feature-flags/hooks/use-enable-product-item-weight-and-dimensions';
import type { ProductOption } from '../utils/get-packaging-requirement';
import { getPackagingRequirement } from '../utils/get-packaging-requirement';
import Global from '../../../../../../store/global-selectors';
import { bulkLoadProductData } from '../../../../../../store/products/products.actions';

import { UnitConversionService } from '../../../../../../services/unit-conversion-service';
import { convertDimensions } from '../../../../../../services/unit-services/dimensions/dimension-convert-service';
import { convertWeight } from '../../../../../../services/unit-services/weights/weight-convert-service';

const safeConvertValue = (value: number, fromUnit: string | undefined, toUnit: string | undefined): number => {
  if (!fromUnit || !toUnit) return value;
  if (fromUnit.toLowerCase() === toUnit.toLowerCase()) return value;
  const converted = UnitConversionService.convertValue(value, fromUnit, toUnit);
  return Number.isNaN(converted) ? value : converted;
};

type UseOrderDataReturn = {
  order: { orderId?: string; items: Record<string, unknown>[] };
  availablePackages: Array<Record<string, unknown>>;
  sellerDimensionUnit: string;
  sellerWeightUnit: string;
  hasMissingProducts: boolean;
};

export const useOrderData = (fulfillmentPlanId: string, shouldLoadProducts = false): UseOrderDataReturn => {
  const dispatch = useDispatch();
  const isProductItemDimensionsEnabled = useEnableProductItemWeightAndDimensions();

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const allPackages = useSelector((state) => Global.selectSellerPackages(state)) ?? emptyArray;
  const sellerDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) ?? 'Inches';
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit) ?? 'Ounces';

  const selectedServiceId = fulfillmentPlan?.serviceId;
  const selectedService = useSelector((state) => {
    if (!selectedServiceId) return null;
    const services = Global.selectSellerServices(state) ?? emptyArray;
    return services.find((s: { serviceId: string }) => s.serviceId === selectedServiceId);
  });

  const availablePackages = useMemo(() => {
    const filtered = !selectedService?.carrierId
      ? allPackages.filter((pkg) => pkg.isCustom && !pkg.isHidden)
      : allPackages.filter((pkg) => {
          if (pkg.isHidden) return false;
          return pkg.isCustom || pkg.carrierId === selectedService.carrierId;
        });

    // Package dimensions/weights are stored in Inches/Ounces — convert to seller's unit
    return filtered.map((pkg: Record<string, unknown>) => {
      const dims = convertDimensions(pkg.dimensions as Record<string, unknown>, sellerDimensionUnit);
      const maxWeight = pkg.maxWeight ? convertWeight(pkg.maxWeight as Record<string, unknown>, sellerWeightUnit) : pkg.maxWeight;
      const tareWeight = pkg.tareWeight ? convertWeight(pkg.tareWeight as Record<string, unknown>, sellerWeightUnit) : pkg.tareWeight;
      const weightBuffer = pkg.weightBuffer
        ? convertWeight(pkg.weightBuffer as Record<string, unknown>, sellerWeightUnit)
        : pkg.weightBuffer;
      return { ...pkg, dimensions: dims, length: dims.length, width: dims.width, height: dims.height, maxWeight, tareWeight, weightBuffer };
    });
  }, [allPackages, selectedService, sellerDimensionUnit, sellerWeightUnit]);

  const productIds = useMemo(() => {
    const fpItems = fulfillmentPlan?.items ?? emptyArray;
    return fpItems.map((item: { productId?: string }) => item.productId).filter(Boolean);
  }, [fulfillmentPlan?.items]);

  const products = useSelector((state) => (productIds.length > 0 ? Global.selectEntitiesProductsByIds(state, { productIds }) : emptyArray));

  const hasMissingProducts = productIds.length > 0 && products.some((p: unknown) => !p);

  useEffect(() => {
    if (shouldLoadProducts && hasMissingProducts) {
      dispatch(bulkLoadProductData(productIds));
    }
  }, [dispatch, hasMissingProducts, productIds, shouldLoadProducts]);

  const order = useMemo(() => {
    const fpItems = fulfillmentPlan?.items ?? emptyArray;
    const productsMap = Object.fromEntries(
      (products ?? emptyArray)
        .filter((product: { productId?: string }) => product?.productId)
        .map((product: { productId?: string }) => [product.productId, product]),
    );

    const enrichedItems = fpItems.map((item: Record<string, unknown>) => {
      const product = productsMap[item.productId as string] as Record<string, unknown> | undefined;

      let itemLength = (product?.length as number) ?? (item.length as number) ?? 0;
      let itemWidth = (product?.width as number) ?? (item.width as number) ?? 0;
      let itemHeight = (product?.height as number) ?? (item.height as number) ?? 0;
      const orderItemWeight = (item.weight as { value?: number })?.value ?? (item.weight as number) ?? 0;
      let weightValue = orderItemWeight > 0 ? orderItemWeight : ((product?.weight as number) ?? 0);

      if (isProductItemDimensionsEnabled) {
        const itemLengthRaw = product?.itemLength as number | undefined;
        const itemWidthRaw = product?.itemWidth as number | undefined;
        const itemHeightRaw = product?.itemHeight as number | undefined;
        const itemDimUnit = product?.itemDimensionUnitOfMeasureId as string | undefined;

        if (itemLengthRaw && itemWidthRaw && itemHeightRaw) {
          itemLength = safeConvertValue(itemLengthRaw, itemDimUnit, sellerDimensionUnit);
          itemWidth = safeConvertValue(itemWidthRaw, itemDimUnit, sellerDimensionUnit);
          itemHeight = safeConvertValue(itemHeightRaw, itemDimUnit, sellerDimensionUnit);
        }

        const itemWeightRaw = product?.itemWeight as number | undefined;
        const itemWeightUnit = product?.itemWeightUnitOfMeasureId as string | undefined;
        if (itemWeightRaw && itemWeightRaw > 0) {
          weightValue = safeConvertValue(itemWeightRaw, itemWeightUnit, sellerWeightUnit);
        }
      }

      const packagingRequirement = getPackagingRequirement(product?.productOptions as ProductOption[] | undefined);

      return {
        ...item,
        length: itemLength,
        width: itemWidth,
        height: itemHeight,
        weight: { value: weightValue, unit: sellerWeightUnit },
        packagingRequirement,
      };
    });

    return {
      orderId: fulfillmentPlan?.orderId,
      items: enrichedItems,
    };
  }, [fulfillmentPlan, products, sellerWeightUnit, sellerDimensionUnit, isProductItemDimensionsEnabled]);

  return { order, availablePackages, sellerDimensionUnit, sellerWeightUnit, hasMissingProducts };
};
